import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { ConfigurationResult, EngineDetails, HealthStatus } from "core/services/copilotService";
import type { RootState } from "../../store";
import { AIProvider, ConfigMode } from "src/modules/striim-ai-sidebar/components/ai-config-screen/ai-config-screen";

export type { HealthStatus, EngineDetails } from "core/services/copilotService";

interface ConfigFormData{
    mode: ConfigMode;
    provider?: AIProvider;
    model?: string;
    ollamaUrl?: string;
    serverUrl?: string;
    apiKey?: string
}
interface StriimAiState {
    isEnabled: boolean;
    isConfigured: boolean | null;
    copilotUrl: string | null;
    isLoading: boolean;
    healthStatus: HealthStatus | null;
    engineDetails: EngineDetails | null;
    isSidebarOpen: boolean;
    isSidebarMaximized: boolean;
    configFormData:ConfigFormData
    isInitEndpointAccessible: boolean;
    authHeaders: Record<string, string> | null;
}

const initialState: StriimAiState = {
    isEnabled: false,
    isConfigured: null,
    copilotUrl: null,
    isLoading: false,
    healthStatus: null,
    engineDetails: null,
    isSidebarOpen: false,
    isSidebarMaximized: false,
    configFormData:{
        mode:"launch",
        provider:"openai",
        model:"",
        ollamaUrl:"",
        serverUrl:""
    },
    isInitEndpointAccessible: true,
    authHeaders: null
};

const striimAiSlice = createSlice({
    name: "striimAi",
    initialState,
    reducers: {
        setIsEnabledAction: (state, action: PayloadAction<boolean>) => {
            state.isEnabled = action.payload;
        },
        setIsLoadingAction: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setHealthStatusAction: (state, action: PayloadAction<HealthStatus | null>) => {
            state.healthStatus = action.payload;
        },
        setConfigurationAction: (state, action: PayloadAction<ConfigurationResult>) => {
            state.isConfigured = action.payload.isConfigured;
            state.copilotUrl = action.payload.copilotUrl;
            state.healthStatus = action.payload.healthStatus;
            state.engineDetails = action.payload.engineDetails;
        },
        setSidebarOpenAction: (state, action: PayloadAction<boolean>) => {
            state.isSidebarOpen = action.payload;
        },
        toggleSidebarOpenAction: state => {
            state.isSidebarOpen = !state.isSidebarOpen;
            if (state.isSidebarOpen) {
                state.isSidebarMaximized = false;
            }
        },
        setSidebarMaximizedAction: (state, action: PayloadAction<boolean>) => {
            state.isSidebarMaximized = action.payload;
        },
        toggleSidebarMaximizedAction: state => {
            state.isSidebarMaximized = !state.isSidebarMaximized;
        },
        clearConfiguration: state => {
            state.isConfigured = false;
            state.healthStatus = null;
            state.copilotUrl = null;
            state.engineDetails = null;
            state.authHeaders = null;
        },
        setAuthHeadersAction: (state, action: PayloadAction<Record<string, string> | null>) => {
            state.authHeaders = action.payload;
        },
        setConfigFormData:(state,action:PayloadAction<ConfigFormData>)=>{
            state.configFormData=action.payload
        },
        setIsInitEndpointAccessibleAction: (state, action: PayloadAction<boolean>) => {
            state.isInitEndpointAccessible = action.payload;
        }
    }
});

export const {
    setIsEnabledAction,
    setIsLoadingAction,
    setHealthStatusAction,
    setConfigurationAction,
    setSidebarOpenAction,
    toggleSidebarOpenAction,
    setSidebarMaximizedAction,
    toggleSidebarMaximizedAction,
    clearConfiguration,
    setConfigFormData,
    setIsInitEndpointAccessibleAction,
    setAuthHeadersAction
} = striimAiSlice.actions;

export const selectIsEnabled = (state: RootState) => state.striimAi.isEnabled;
export const selectIsConfigured = (state: RootState) => state.striimAi.isConfigured;
export const selectCopilotUrl = (state: RootState) => state.striimAi.copilotUrl;
export const selectIsLoading = (state: RootState) => state.striimAi.isLoading;
export const selectHealthStatus = (state: RootState) => state.striimAi.healthStatus;
export const selectEngineDetails = (state: RootState) => state.striimAi.engineDetails;
export const selectIsHealthy = (state: RootState) =>
    state.striimAi.healthStatus?.status === "healthy" && state.striimAi.healthStatus?.isAuth === true;
export const selectIsSidebarOpen = (state: RootState) => state.striimAi.isSidebarOpen;
export const selectIsSidebarMaximized = (state: RootState) => state.striimAi.isSidebarMaximized;
export const selectIsInitEndpointAccessible = (state: RootState) => state.striimAi.isInitEndpointAccessible;
export const selectAuthHeaders = (state: RootState) => state.striimAi.authHeaders;
export const selectAreAuthHeadersReady = (state: RootState) => state.striimAi.authHeaders !== null;

export default striimAiSlice.reducer;
